import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { motion, AnimatePresence } from 'framer-motion';
import FlightHUD from './FlightHUD';
import MobileControls from './MobileControls';
import CheckpointPanel from './CheckpointPanel';
import FlightComplete from './FlightComplete';
import { CHECKPOINTS } from '../data/flightCheckpoints';

const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768);

const FlightMode = ({ onExit, onNavigateToSection }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsRef = useRef({ joyX: 0, joyY: 0, throttle: 0.45 });
  const keysRef = useRef({});
  const keysDownRef = useRef({});   // for edge detection

  const [hudData, setHudData] = useState({
    heading: 0, altitude: 20, speed: 0, throttle: 0.45,
    checkpointsVisited: 0, compassBearing: 0, distanceToNext: null,
    offCourse: false, pitch: 0, bank: 0,
  });

  const [activeCheckpoint, setActiveCheckpoint] = useState(null);
  const [visitedCount, setVisitedCount] = useState(0);
  const [missionDone, setMissionDone] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [isMobile, setIsMobile] = useState(isMobileDevice);

  useEffect(() => {
    const down = (e) => {
      const k = e.key.toLowerCase();
      keysRef.current[k] = true;
      if (!keysDownRef.current[k]) keysDownRef.current[k] = true;
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(k)) e.preventDefault();
      setShowHint(false);
    };
    const up = (e) => {
      const k = e.key.toLowerCase();
      keysRef.current[k] = false;
      keysDownRef.current[k] = false;
    };
    window.addEventListener('keydown', down, { passive: false });
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  useEffect(() => { const t = setTimeout(() => setShowHint(false), 8000); return () => clearTimeout(t); }, []);
  useEffect(() => {
    const check = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ───── Three.js scene ─────
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !THREE) return;

    const scene = new THREE.Scene();
    const W = mount.clientWidth || window.innerWidth;
    const H = mount.clientHeight || window.innerHeight;
    const camera = new THREE.PerspectiveCamera(55, W / Math.max(1, H), 0.1, 2000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setClearColor(0x07101e, 1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2));
    renderer.setSize(W, H);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.85;
    mount.appendChild(renderer.domElement);

    const clock = new THREE.Clock();

    const pathPts = [
      new THREE.Vector3(0, 22, 20),
      new THREE.Vector3(45, 32, -60),
      new THREE.Vector3(-30, 44, -150),
      new THREE.Vector3(50, 24, -240),
      new THREE.Vector3(-20, 38, -340),
      new THREE.Vector3(25, 30, -440),
      new THREE.Vector3(0, 34, -520),
    ];
    const refCurve = new THREE.CatmullRomCurve3(pathPts);
    const curveSamples = [];
    for (let i = 0; i <= 80; i++) curveSamples.push(refCurve.getPointAt(i / 80));

    function frameAt(tt) {
      const cl = Math.max(0, Math.min(1, tt));
      const point = refCurve.getPointAt(cl);
      const tangent = refCurve.getTangentAt(cl).clone().normalize();
      let up = new THREE.Vector3(0, 1, 0);
      let right = new THREE.Vector3().crossVectors(tangent, up);
      if (right.lengthSq() < 1e-6) right = new THREE.Vector3(1, 0, 0); else right.normalize();
      up = new THREE.Vector3().crossVectors(right, tangent).normalize();
      return { point, tangent, right, up };
    }

    function makeCanvasTex(draw, w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      draw(c.getContext('2d'), w, h);
      return new THREE.CanvasTexture(c);
    }

    function buildSky() {
      const geo = new THREE.SphereGeometry(1400, 32, 20);
      const cols = [];
      const zenith = new THREE.Color(0x04080f);
      const upper = new THREE.Color(0x0b1d35);
      const midc = new THREE.Color(0x163356);
      const horizon = new THREE.Color(0x1e4878);
      const sunGlow = new THREE.Color(0xe8813a);
      const pos = geo.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const y = pos.getY(i) / 1400;
        let c;
        if (y > 0.65) c = upper.clone().lerp(zenith, (y - 0.65) / 0.35);
        else if (y > 0.25) c = midc.clone().lerp(upper, (y - 0.25) / 0.4);
        else if (y > -0.05) c = horizon.clone().lerp(midc, (y + 0.05) / 0.3);
        else { const t2 = Math.min(1, (-y) * 4); c = sunGlow.clone().lerp(horizon, t2); }
        cols.push(c.r, c.g, c.b);
      }
      geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
      scene.add(new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, fog: false })));

      const sunTex = makeCanvasTex((ctx, w, h) => {
        const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
        g.addColorStop(0, 'rgba(255,252,230,1)'); g.addColorStop(0.18, 'rgba(255,218,130,0.92)');
        g.addColorStop(0.55, 'rgba(255,155,50,0.38)'); g.addColorStop(1, 'rgba(255,100,20,0)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }, 256, 256);
      const sun = new THREE.Sprite(new THREE.SpriteMaterial({ map: sunTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
      sun.scale.set(320, 320, 1); sun.position.set(-280, 210, -580); scene.add(sun);

      const hGlowTex = makeCanvasTex((ctx, w, h) => {
        const g = ctx.createLinearGradient(0, 0, 0, h);
        g.addColorStop(0, 'rgba(255,130,30,0)'); g.addColorStop(0.6, 'rgba(255,100,20,0.18)'); g.addColorStop(1, 'rgba(255,80,10,0.4)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }, 4, 256);
      const hGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: hGlowTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
      hGlow.scale.set(2200, 500, 1); hGlow.position.set(-180, -60, -720); scene.add(hGlow);
    }

    function buildStars() {
      const sTex = makeCanvasTex((ctx, w, h) => {
        const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
        g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.28, 'rgba(210,225,255,0.85)'); g.addColorStop(1, 'rgba(200,215,255,0)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }, 32, 32);
      const sMat = new THREE.SpriteMaterial({ map: sTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
      for (let i = 0; i < (isMobile ? 130 : 240); i++) {
        const s = new THREE.Sprite(sMat.clone());
        const sc = 1.2 + Math.random() * 3.2; s.scale.set(sc, sc, 1);
        const th = Math.random() * Math.PI * 2;
        const hf = 0.28 + Math.random() * 0.68;
        const r = 1200;
        s.position.set(Math.cos(th) * r * (1 - hf * 0.18), 60 + hf * 980, Math.sin(th) * r * (1 - hf * 0.18));
        s.material.opacity = 0.12 + Math.random() * 0.72;
        scene.add(s);
      }
    }

    function buildClouds() {
      const cTex = makeCanvasTex((ctx, w, h) => {
        const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
        g.addColorStop(0, 'rgba(215,232,255,0.94)'); g.addColorStop(0.32, 'rgba(195,220,248,0.52)');
        g.addColorStop(0.68, 'rgba(175,208,240,0.18)'); g.addColorStop(1, 'rgba(160,200,238,0)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }, 256, 256);
      const cMat = new THREE.SpriteMaterial({ map: cTex, transparent: true, depthWrite: false, fog: true });
      for (let i = 0; i < (isMobile ? 44 : 90); i++) {
        const s = new THREE.Sprite(cMat.clone());
        const sz = 30 + Math.random() * 72; s.scale.set(sz, sz * 0.45, 1);
        const tA = Math.random(); const f = frameAt(tA);
        const sd = (Math.random() - 0.5) * 210;
        const vt = (Math.random() - 0.22) * 72;
        s.position.copy(f.point).addScaledVector(f.right, sd).addScaledVector(f.up, vt);
        s.material.opacity = 0.38 + Math.random() * 0.5;
        scene.add(s);
      }
    }

    function buildGround() {
      const gTex = makeCanvasTex((ctx, w, h) => {
        ctx.fillStyle = '#091822'; ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = 'rgba(80,170,220,0.09)'; ctx.lineWidth = 1.2;
        const step = 44;
        for (let x = 0; x <= w; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
        for (let y = 0; y <= h; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
      }, 512, 512);
      gTex.wrapS = gTex.wrapT = THREE.RepeatWrapping; gTex.repeat.set(90, 90);
      const ground = new THREE.Mesh(new THREE.PlaneGeometry(3600, 3600),
        new THREE.MeshStandardMaterial({ map: gTex, roughness: 1, metalness: 0 }));
      ground.rotation.x = -Math.PI / 2; ground.position.y = -14; ground.receiveShadow = true;
      scene.add(ground);
    }

    function buildMountains() {
      const n = isMobile ? 28 : 52; const r = 720;
      const positions = [], colors = [];
      const valley = new THREE.Color(0x060f1a);
      const ridge = new THREE.Color(0x183450);
      const lit = new THREE.Color(0x5a4a3a);
      for (let i = 0; i <= n; i++) {
        const a = (i / n) * Math.PI * 2;
        const peak = 28 + Math.random() * 95;
        const x = Math.cos(a) * r; const z = Math.sin(a) * r;
        positions.push(x, -14, z, x, peak - 14, z);
        const lf = Math.max(0, Math.cos(a - 0.4)) * 0.65;
        const top = ridge.clone().lerp(lit, lf);
        colors.push(valley.r, valley.g, valley.b, top.r, top.g, top.b);
      }
      const idx = [];
      for (let i = 0; i < n; i++) { const a0 = i * 2, b0 = i * 2 + 1, a1 = (i + 1) * 2, b1 = (i + 1) * 2 + 1; idx.push(a0, a1, b0, b0, a1, b1); }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geo.setIndex(idx); geo.computeVertexNormals();
      scene.add(new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: true })));
    }

    scene.add(new THREE.HemisphereLight(0xb8d8f0, 0x0d1e30, 0.95));
    const sunLight = new THREE.DirectionalLight(0xffd090, 1.25);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(1024, 1024);
    Object.assign(sunLight.shadow.camera, { left: -110, right: 110, top: 110, bottom: -110, near: 0.5, far: 500 });
    sunLight.shadow.bias = -0.001;
    scene.add(sunLight); scene.add(sunLight.target);
    scene.fog = new THREE.FogExp2(0x163356, 0.0022);

    buildSky(); buildStars(); buildMountains(); buildGround(); buildClouds();

    const buildingBoxes = [];

    function buildCity() {
      const winTex = makeCanvasTex((ctx, w, h) => {
        ctx.fillStyle = '#050c18'; ctx.fillRect(0, 0, w, h);
        const cols = 6, rows = 14, cw = w / cols, rh = h / rows;
        for (let row = 0; row < rows; row++) for (let col = 0; col < cols; col++) {
          if (Math.random() < 0.36) continue;
          const hue = Math.random() > 0.62 ? 42 : (Math.random() > 0.5 ? 198 : 218);
          ctx.fillStyle = `hsla(${hue},78%,76%,${0.48 + Math.random() * 0.52})`;
          ctx.fillRect(col * cw + 2, row * rh + 2, cw - 4, rh - 4);
        }
      }, 128, 256);
      const winTex2 = makeCanvasTex((ctx, w, h) => {
        ctx.fillStyle = '#060d1c'; ctx.fillRect(0, 0, w, h);
        const cols = 4, rows = 20, cw = w / cols, rh = h / rows;
        for (let row = 0; row < rows; row++) for (let col = 0; col < cols; col++) {
          if (Math.random() < 0.4) continue;
          ctx.fillStyle = `hsla(${198 + Math.random() * 38},88%,78%,${0.42 + Math.random() * 0.58})`;
          ctx.fillRect(col * cw + 2, row * rh + 2, cw - 4, rh - 4);
        }
      }, 64, 256);

      const faceA = new THREE.MeshStandardMaterial({ map: winTex, roughness: 0.82, metalness: 0.38 });
      const faceB = new THREE.MeshStandardMaterial({ map: winTex2, roughness: 0.72, metalness: 0.48 });
      const roofM = new THREE.MeshStandardMaterial({ color: 0x091624, roughness: 0.92, metalness: 0.28 });
      const glassM = new THREE.MeshStandardMaterial({
        color: 0x5a72e0, emissive: 0x1e2e8a, emissiveIntensity: 0.48,
        transparent: true, opacity: 0.52, roughness: 0.08, metalness: 0.85
      });
      const spireM = new THREE.MeshStandardMaterial({ color: 0xff5c38, emissive: 0xff3a18, emissiveIntensity: 2.2 });

      function makeBuilding(bx, bz, bw, bd, bh, variant) {
        const grp = new THREE.Group();
        const mat = variant % 2 === 0 ? faceA : faceB;
        const body = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bd),
          [mat, mat, roofM, roofM, mat, mat]);
        body.position.y = bh / 2 - 14;
        body.castShadow = true; body.receiveShadow = true;
        grp.add(body);
        buildingBoxes.push({
          cx: bx, cy: bh / 2 - 14, cz: bz,
          hx: bw / 2 + 1.5, hy: bh / 2 + 2, hz: bd / 2 + 1.5,
        });
        if (bh > 38 && Math.random() > 0.32) {
          const spH = bh * 0.30;
          const sp = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.32, spH, 8),
            new THREE.MeshStandardMaterial({ color: 0x7aabf5, emissive: 0x1e42a8, emissiveIntensity: 0.65, metalness: 0.92, roughness: 0.08 }));
          sp.position.y = bh - 14 + spH * 0.5;
          grp.add(sp);
          const tip = new THREE.Mesh(new THREE.SphereGeometry(0.42, 6, 6), spireM);
          tip.position.y = bh - 14 + spH; tip.userData.isBlink = true;
          grp.add(tip);
        }
        if (variant % 3 === 0 && bh > 26) {
          const gf = new THREE.Mesh(new THREE.BoxGeometry(bw * 0.52, bh * 0.72, 0.32), glassM);
          gf.position.set(0, bh / 2 - 14, bd / 2 + 0.18); grp.add(gf);
        }
        grp.position.set(bx, 0, bz); scene.add(grp); return grp;
      }

      const clusters = [
        { t: 0.06, side: -1, n: isMobile ? 7 : 14, hMin: 14, hMax: 54 }, { t: 0.06, side: 1, n: isMobile ? 6 : 12, hMin: 12, hMax: 46 },
        { t: 0.20, side: -1, n: isMobile ? 9 : 16, hMin: 20, hMax: 74 }, { t: 0.20, side: 1, n: isMobile ? 8 : 14, hMin: 18, hMax: 64 },
        { t: 0.36, side: -1, n: isMobile ? 8 : 15, hMin: 24, hMax: 86 }, { t: 0.36, side: 1, n: isMobile ? 7 : 13, hMin: 22, hMax: 78 },
        { t: 0.52, side: -1, n: isMobile ? 10 : 18, hMin: 22, hMax: 96 }, { t: 0.52, side: 1, n: isMobile ? 9 : 16, hMin: 20, hMax: 90 },
        { t: 0.68, side: -1, n: isMobile ? 8 : 15, hMin: 16, hMax: 70 }, { t: 0.68, side: 1, n: isMobile ? 7 : 13, hMin: 15, hMax: 64 },
        { t: 0.84, side: -1, n: isMobile ? 8 : 15, hMin: 20, hMax: 80 }, { t: 0.84, side: 1, n: isMobile ? 8 : 14, hMin: 18, hMax: 74 },
      ];
      clusters.forEach((cl, ci) => {
        const f = frameAt(cl.t);
        for (let i = 0; i < cl.n; i++) {
          const lat = cl.side * (28 + Math.random() * 34) + (Math.random() - 0.5) * 18;
          const along = (Math.random() - 0.5) * 44;
          const bx = f.point.x + f.right.x * lat + f.tangent.x * along;
          const bz = f.point.z + f.right.z * lat + f.tangent.z * along;
          const bw = 4.5 + Math.random() * 11;
          const bd = 4.5 + Math.random() * 11;
          const bh = cl.hMin + Math.random() * (cl.hMax - cl.hMin);
          makeBuilding(bx, bz, bw, bd, bh, ci * 8 + i);
        }
      });

      const gTex = makeCanvasTex((ctx, w, h) => {
        const gg = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
        gg.addColorStop(0, 'rgba(90,112,225,0.26)'); gg.addColorStop(0.5, 'rgba(90,112,225,0.06)');
        gg.addColorStop(1, 'rgba(90,112,225,0)');
        ctx.fillStyle = gg; ctx.fillRect(0, 0, w, h);
      }, 128, 128);
      const gsMat = new THREE.SpriteMaterial({ map: gTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
      clusters.filter((_, i) => i % 2 === 0).forEach(cl => {
        const f = frameAt(cl.t);
        const gs = new THREE.Sprite(gsMat.clone()); gs.scale.set(120, 120, 1);
        gs.position.set(f.point.x + f.right.x * cl.side * 38, -13, f.point.z + f.right.z * cl.side * 38);
        scene.add(gs);
      });

      const rMat = new THREE.MeshBasicMaterial({ color: 0x5a72e0, transparent: true, opacity: 0.09 });
      for (let ri = 0; ri < (isMobile ? 10 : 22); ri++) {
        const t0 = ri / 22; const f0 = frameAt(t0);
        const sd = (Math.random() - 0.5) * 58;
        const road = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.22, 58), rMat);
        road.position.set(f0.point.x + f0.right.x * sd, -13.6, f0.point.z + f0.right.z * sd);
        road.rotation.y = Math.atan2(f0.tangent.x, f0.tangent.z); scene.add(road);
      }
    }

    const labelSprites = [];
    const labelWorldPos = [];

    function buildCheckpointLabel(worldPos, labelText, cpColor, tangent) {
      const cObj = new THREE.Color(cpColor);
      const cr = Math.round(cObj.r * 255), cg2 = Math.round(cObj.g * 255), cb = Math.round(cObj.b * 255);

      const tex = makeCanvasTex((ctx, w, h) => {
        ctx.clearRect(0, 0, w, h);
        const rx = 20;
        ctx.beginPath();
        ctx.moveTo(rx, 3); ctx.lineTo(w - rx, 3); ctx.quadraticCurveTo(w - 3, 3, w - 3, rx);
        ctx.lineTo(w - 3, h - rx - 3); ctx.quadraticCurveTo(w - 3, h - 3, w - rx, h - 3);
        ctx.lineTo(rx, h - 3); ctx.quadraticCurveTo(3, h - 3, 3, h - rx - 3);
        ctx.lineTo(3, rx); ctx.quadraticCurveTo(3, 3, rx, 3); ctx.closePath();
        const bg = ctx.createLinearGradient(0, 0, 0, h);
        bg.addColorStop(0, 'rgba(8,16,30,0.92)'); bg.addColorStop(1, 'rgba(4,10,20,0.95)');
        ctx.fillStyle = bg; ctx.fill();
        ctx.strokeStyle = `rgba(${cr},${cg2},${cb},0.75)`; ctx.lineWidth = 3; ctx.stroke();
        const dotX = 40;
        const dg = ctx.createRadialGradient(dotX, h / 2, 0, dotX, h / 2, 10);
        dg.addColorStop(0, `rgba(${cr},${cg2},${cb},1)`); dg.addColorStop(1, `rgba(${cr},${cg2},${cb},0)`);
        ctx.fillStyle = dg; ctx.beginPath(); ctx.arc(dotX, h / 2, 10, 0, Math.PI * 2); ctx.fill();
        ctx.font = 'bold 36px Inter, "Helvetica Neue", sans-serif';
        ctx.fillStyle = `rgba(${cr},${cg2},${cb},1)`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText(labelText, 62, h / 2);
        ctx.font = '500 22px Inter, sans-serif';
        ctx.fillStyle = `rgba(${cr},${cg2},${cb},0.42)`;
        ctx.textAlign = 'right';
        ctx.fillText('●', w - 16, h / 2);
      }, 400, 78);

      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, fog: false }));
      sp.scale.set(40, 9.5, 1);
      sp.position.set(worldPos.x, worldPos.y + 22, worldPos.z);
      scene.add(sp);
      labelSprites.push(sp);
      labelWorldPos.push(worldPos.clone());
      return sp;
    }

    buildCity();

    function buildPlane() {
      const root = new THREE.Group();
      const visual = new THREE.Group();
      root.add(visual);

      const bodyM = new THREE.MeshStandardMaterial({ color: 0xcdd8e8, metalness: 0.62, roughness: 0.28 });
      const darkM = new THREE.MeshStandardMaterial({ color: 0x18242e, metalness: 0.58, roughness: 0.38 });
      const accentM = new THREE.MeshStandardMaterial({ color: 0x5570e8, emissive: 0x1e2e90, emissiveIntensity: 0.85 });
      const cockpitM = new THREE.MeshStandardMaterial({ color: 0x5ee8f8, emissive: 0x12849a, emissiveIntensity: 0.72, transparent: true, opacity: 0.82, roughness: 0.05, metalness: 0.9 });
      const exhaustM = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.9 });
      const redM = new THREE.MeshStandardMaterial({ color: 0xff3a3a, emissive: 0xaa0808, emissiveIntensity: 0.9 });
      const greenM = new THREE.MeshStandardMaterial({ color: 0x30ff80, emissive: 0x0a9940, emissiveIntensity: 0.9 });
      const whiteM = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 1.4 });

      const nose = new THREE.Mesh(new THREE.ConeGeometry(0.72, 4.2, 14, 1), bodyM);
      nose.rotation.x = -Math.PI / 2; nose.position.z = -2.0; visual.add(nose);
      const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.72, 3.4, 14, 1), bodyM);
      tube.rotation.x = Math.PI / 2; tube.position.z = 0.8; visual.add(tube);
      const taper = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.24, 3.2, 14, 1), bodyMshared());
      function bodyMshared() { return bodyM; }
      taper.rotation.x = Math.PI / 2; taper.position.z = 4.0; visual.add(taper);

      const cockpit = new THREE.Mesh(new THREE.SphereGeometry(0.48, 12, 10, 0, Math.PI * 2, 0, Math.PI / 1.55), cockpitM);
      cockpit.rotation.x = Math.PI; cockpit.position.set(0, 0.52, -0.85);
      cockpit.scale.set(1.1, 0.78, 2.1); visual.add(cockpit);

      function makeWing(side) {
        const g = new THREE.Group();
        const inner = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.18, 2.0), darkM);
        inner.position.set(side * 1.1, -0.08, 0.55); inner.castShadow = true; g.add(inner);
        const outer = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.12, 1.4), darkM);
        outer.position.set(side * 3.1, -0.10, 1.15);
        outer.rotation.y = side * 0.22;
        outer.castShadow = true; g.add(outer);
        const tip = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.1, 0.6), accentM);
        tip.position.set(side * 4.5, -0.09, 1.35); tip.rotation.y = side * 0.38; g.add(tip);
        const le = new THREE.Mesh(new THREE.BoxGeometry(side > 0 ? 4.8 : 4.8, 0.06, 0.22), accentM);
        le.position.set(side * 2.4, -0.05, -0.28); le.rotation.y = side * 0.14; g.add(le);
        return g;
      }
      visual.add(makeWing(-1));
      visual.add(makeWing(1));

      const htail = new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.10, 0.9), darkM);
      htail.position.set(0, 0.04, 4.2); visual.add(htail);
      const elev = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.07, 0.42), accentM);
      elev.position.set(0, 0.04, 4.5); visual.add(elev);

      const vfinG = new THREE.BufferGeometry();
      const vPts = new Float32Array([
        0, 0, 2.4, 0, 0, 4.9, 0, 1.65, 4.85,
        0, 0, 2.4, 0, 1.65, 4.85, 0, 0.5, 3.5,
      ]);
      vfinG.setAttribute('position', new THREE.Float32BufferAttribute(vPts, 3));
      vfinG.computeVertexNormals();
      const vfinL = new THREE.Mesh(vfinG, darkM); vfinL.position.x = -0.055; visual.add(vfinL);
      const vfinR = new THREE.Mesh(vfinG, darkM); vfinR.position.x = 0.055; visual.add(vfinR);
      const rudder = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.8, 0.72), accentM);
      rudder.position.set(0, 1.1, 4.6); visual.add(rudder);

      [-1.9, 1.9].forEach(x => {
        const nac = new THREE.Mesh(new THREE.CylinderGeometry(0.30, 0.24, 2.1, 12), bodyM);
        nac.rotation.x = Math.PI / 2; nac.position.set(x, -0.28, 0.75); nac.castShadow = true; visual.add(nac);
        const ring = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.04, 8, 20), darkM);
        ring.position.set(x, -0.28, -0.35); visual.add(ring);
        const ex = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 0.28, 10), exhaustM);
        ex.rotation.x = Math.PI / 2; ex.position.set(x, -0.28, 1.9); visual.add(ex);
        const glow = new THREE.Mesh(new THREE.CircleGeometry(0.17, 10),
          new THREE.MeshStandardMaterial({ color: 0xff8c40, emissive: 0xff5010, emissiveIntensity: 1.8, side: THREE.DoubleSide }));
        glow.rotation.x = Math.PI / 2; glow.position.set(x, -0.28, 2.05); visual.add(glow);
      });

      const lLight = new THREE.Mesh(new THREE.SphereGeometry(0.13, 8, 8), redM);
      lLight.position.set(-4.6, -0.09, 1.35); visual.add(lLight);
      const rLight = new THREE.Mesh(new THREE.SphereGeometry(0.13, 8, 8), greenM);
      rLight.position.set(4.6, -0.09, 1.35); visual.add(rLight);
      const strobe = new THREE.Mesh(new THREE.SphereGeometry(0.11, 8, 8), whiteM);
      strobe.position.set(0, 0.78, 5.0); visual.add(strobe);

      visual.userData.strobe = strobe;
      root.traverse(o => { if (o.isMesh) o.castShadow = true; });
      scene.add(root);
      return { group: root, visual };
    }

    const { group: planeGroup, visual: planeVisual } = buildPlane();

    const CONTRAIL_N = isMobile ? 26 : 52;
    const contrailPool = [];
    let contrailIdx = 0, contrailTimer = 0;
    const cTex2 = makeCanvasTex((ctx, w, h) => {
      const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
      g.addColorStop(0, 'rgba(255,255,255,0.75)'); g.addColorStop(0.38, 'rgba(210,228,255,0.28)');
      g.addColorStop(1, 'rgba(200,220,255,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    }, 64, 64);
    for (let i = 0; i < CONTRAIL_N; i++) {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: cTex2, transparent: true, depthWrite: false, fog: true, opacity: 0 }));
      s.visible = false; s.userData.life = 0; scene.add(s); contrailPool.push(s);
    }

    function makeCheckpointRing(colorHex) {
      const grp = new THREE.Group();
      const mat = new THREE.MeshStandardMaterial({
        color: colorHex, emissive: colorHex, emissiveIntensity: 1.4, roughness: 0.22, metalness: 0.42,
      });
      const ring = new THREE.Mesh(new THREE.TorusGeometry(8.5, 0.48, 18, 54), mat);
      ring.castShadow = true; grp.add(ring);

      const c = new THREE.Color(colorHex);
      const iFillTex = makeCanvasTex((ctx, w, h) => {
        const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
        const rr = Math.round(c.r * 255), gg = Math.round(c.g * 255), bb = Math.round(c.b * 255);
        g.addColorStop(0, `rgba(${rr},${gg},${bb},0.22)`);
        g.addColorStop(0.5, `rgba(${rr},${gg},${bb},0.08)`);
        g.addColorStop(1, `rgba(${rr},${gg},${bb},0)`);
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }, 128, 128);
      const fill = new THREE.Sprite(new THREE.SpriteMaterial({ map: iFillTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
      fill.scale.set(26, 26, 1); grp.add(fill);

      const bTex = makeCanvasTex((ctx, w, h) => {
        const g = ctx.createLinearGradient(0, h, 0, 0);
        g.addColorStop(0, `rgba(255,200,100,0.92)`); g.addColorStop(0.12, `rgba(255,185,80,0.58)`);
        g.addColorStop(0.5, `rgba(255,172,60,0.2)`); g.addColorStop(1, `rgba(255,162,40,0)`);
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }, 16, 512);
      const bMat = new THREE.MeshBasicMaterial({ map: bTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false });
      const beacon = new THREE.Group();
      for (let bi = 0; bi < 3; bi++) {
        const p = new THREE.Mesh(new THREE.PlaneGeometry(7, 380), bMat);
        p.rotation.y = (Math.PI / 3) * bi; p.position.y = 190; beacon.add(p);
      }
      grp.userData.beacon = beacon; grp.add(beacon);
      grp.userData.ring = ring;
      grp.userData.fill = fill;
      grp.userData.mat = mat;
      grp.userData.bMat = bMat;
      return grp;
    }

    const cpPositions = [0.04, 0.24, 0.48, 0.72, 0.96];
    const cpObjects = cpPositions.map((tt, i) => {
      const cp = CHECKPOINTS[i];
      const f = frameAt(tt);
      const col = cp.color || '#667eea';
      const obj = makeCheckpointRing(col);
      obj.position.copy(f.point);
      obj.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), f.tangent);
      scene.add(obj);
      buildCheckpointLabel(f.point, cp.label, col, f.tangent);
      return {
        t: tt, worldPos: f.point.clone(), normal: f.tangent.clone(),
        radius: 10.5, visited: false, insideNow: false, object: obj, data: cp, index: i
      };
    });

    let throttle = 0.45;
    const MIN_SPEED = 4, MAX_SPEED = 48;

    let planePos = new THREE.Vector3(0, 22, 20);
    let headingAngle = 0, pitchAngle = 0;
    let yawVel = 0, pitchVel = 0, bankAngle = 0;
    let paused = false, shakeTime = 0;

    const ANG_ACCEL = 3.0, ANG_DAMP = 2.8;
    const MAX_YAW = 1.05, MAX_PITCH = 0.75, MAX_PITCH_ANGLE = 0.90;
    const THROTTLE_RATE = 0.8;

    function steerInput() {
      const k = keysRef.current;
      const c = controlsRef.current;
      if (k['['] || k['pagedown']) throttle = Math.max(0, throttle - THROTTLE_RATE * 0.016);
      if (k[']'] || k['pageup']) throttle = Math.min(1, throttle + THROTTLE_RATE * 0.016);
      if (typeof c.throttle === 'number') throttle = c.throttle;
      let x = 0, y = 0;
      if (k['arrowleft'] || k['a']) x -= 1;
      if (k['arrowright'] || k['d']) x += 1;
      if (k['arrowup'] || k['w']) y += 1;
      if (k['arrowdown'] || k['s']) y -= 1;
      if (Math.abs(c.joyX) > 0.04) x = c.joyX;
      if (Math.abs(c.joyY) > 0.04) y = c.joyY;
      return { x, y };
    }

    function updateFlight(delta) {
      const inp = steerInput();
      if (keysRef.current['['] || keysRef.current['pagedown']) throttle = Math.max(0, throttle - THROTTLE_RATE * delta);
      if (keysRef.current[']'] || keysRef.current['pageup']) throttle = Math.min(1, throttle + THROTTLE_RATE * delta);

      yawVel += inp.x * ANG_ACCEL * delta;
      pitchVel += inp.y * ANG_ACCEL * delta;
      yawVel -= yawVel * ANG_DAMP * delta;
      pitchVel -= pitchVel * ANG_DAMP * delta;
      yawVel = Math.max(-MAX_YAW, Math.min(MAX_YAW, yawVel));
      pitchVel = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, pitchVel));

      if (!paused) {
        headingAngle -= yawVel * delta;
        pitchAngle = Math.max(-MAX_PITCH_ANGLE, Math.min(MAX_PITCH_ANGLE, pitchAngle + pitchVel * delta));
      }

      bankAngle += ((-yawVel * 0.62) - bankAngle) * Math.min(1, delta * 5.5);

      const qYaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), headingAngle);
      const qPitch = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), pitchAngle);
      const planeQuat = qYaw.clone().multiply(qPitch);
      planeGroup.quaternion.copy(planeQuat);

      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(planeQuat);
      if (!paused) {
        const speed = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * throttle;
        planePos.addScaledVector(forward, speed * delta);
        planePos.y = Math.max(-10, planePos.y);
      }
      planeGroup.position.copy(planePos);
      planeVisual.rotation.z = bankAngle;

      for (const b of buildingBoxes) {
        const dx = Math.abs(planePos.x - b.cx) - b.hx;
        const dy = Math.abs(planePos.y - b.cy) - b.hy;
        const dz = Math.abs(planePos.z - b.cz) - b.hz;
        if (dx < 0 && dy < 0 && dz < 0) {
          const px = dx + b.hx, py = dy + b.hy, pz = dz + b.hz;
          if (px > py && px > pz) { planePos.x += planePos.x > b.cx ? b.hx - Math.abs(planePos.x - b.cx) + 0.5 : -(b.hx - Math.abs(planePos.x - b.cx) + 0.5); }
          else if (py > pz) { planePos.y = b.cy + b.hy + 1.5; pitchVel = Math.max(pitchVel, 0.3); }
          else { planePos.z += planePos.z > b.cz ? b.hz - Math.abs(planePos.z - b.cz) + 0.5 : -(b.hz - Math.abs(planePos.z - b.cz) + 0.5); }
          shakeTime = 0.4;
        }
      }

      if (planeVisual.userData.strobe)
        planeVisual.userData.strobe.material.emissiveIntensity = 0.2 + Math.abs(Math.sin(clock.elapsedTime * 5.8)) * 1.8;

      return { planeQuat, forward };
    }

    const camPos = new THREE.Vector3();
    const camLook = new THREE.Vector3();
    function updateCamera(instant) {
      const q = planeGroup.quaternion;
      const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(q);
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(q);
      const target = planePos.clone().addScaledVector(fwd, -19).addScaledVector(up, 5.8);
      if (shakeTime > 0) { target.x += (Math.random() - 0.5) * 0.4; target.y += (Math.random() - 0.5) * 0.4; }
      if (instant) { camPos.copy(target); camera.position.copy(target); }
      else { camPos.lerp(target, 0.088); camera.position.copy(camPos); }
      camLook.copy(planePos.clone().addScaledVector(fwd, 17).addScaledVector(up, 1.5));
      camera.lookAt(camLook);
    }

    function updateSun() {
      sunLight.position.copy(planePos).add(new THREE.Vector3(-85, 105, 55));
      sunLight.target.position.copy(planePos);
      sunLight.target.updateMatrixWorld();
    }

    function updateContrail(delta) {
      contrailTimer -= delta;
      const rate = 0.018 + (1 - throttle) * 0.06;
      if (!paused && contrailTimer <= 0) {
        contrailTimer = rate;
        const tailOff = new THREE.Vector3(0, 0, 5.2).applyQuaternion(planeGroup.quaternion);
        const s = contrailPool[contrailIdx]; contrailIdx = (contrailIdx + 1) % contrailPool.length;
        s.position.copy(planePos).add(tailOff); s.visible = true; s.userData.life = 1.0; s.scale.set(1.6, 1.6, 1);
      }
      contrailPool.forEach(s => {
        if (!s.visible) return;
        s.userData.life -= delta * 0.45;
        if (s.userData.life <= 0) { s.visible = false; return; }
        s.material.opacity = s.userData.life * (0.15 + throttle * 0.4);
        const sc = 1.6 + (1 - s.userData.life) * 7; s.scale.set(sc, sc, 1);
      });
    }

    // Ring animation. Unvisited checkpoints get the eager pulse, next one
    // pulses fastest. Already-visited checkpoints keep a calm steady glow
    // instead of fading out, since they can be flown back through anytime.
    function updateRings(delta) {
      const nextI = cpObjects.findIndex(c => !c.visited);
      cpObjects.forEach((cp, i) => {
        const ring = cp.object.userData.ring; if (!ring) return;
        const t2 = clock.elapsedTime;

        if (cp.visited) {
          ring.rotation.z = t2 * 0.12;
          ring.material.emissiveIntensity = 1.1 + Math.sin(t2 * 1.4 + i) * 0.25;
          ring.scale.setScalar(1);
          if (cp.object.userData.beacon) {
            cp.object.userData.bMat.opacity = 0.35 + Math.sin(t2 * 1.4 + i) * 0.15;
            cp.object.userData.beacon.rotation.y += delta * 0.12;
          }
          return;
        }

        const isNext = i === nextI;
        ring.rotation.z = t2 * (isNext ? 0.65 : 0.18);
        ring.material.emissiveIntensity = isNext
          ? 1.2 + Math.sin(t2 * 2.8) * 0.6
          : 0.6 + Math.sin(t2 * 1.1 + i) * 0.2;
        ring.scale.setScalar(isNext ? 1 + Math.sin(t2 * 2) * 0.04 : 1);
        if (cp.object.userData.beacon) {
          cp.object.userData.bMat.opacity = 0.65 + Math.sin(t2 * 2.2 + i) * 0.35;
          cp.object.userData.beacon.rotation.y += delta * (isNext ? 0.45 : 0.18);
        }
      });
    }

    // Collisions. Fires on every fresh entry into a checkpoint's ring,
    // whether or not it has already been visited, so sections can always
    // be revisited. An insideNow flag debounces so lingering inside the
    // ring, or being paused inside it while the panel is open, does not
    // repeatedly retrigger the panel. This function only ever pauses
    // flight and opens a panel, it never decides mission completion, see
    // handleContinue below for why that split matters.
    function checkCollisions(setCP, setVC) {
      cpObjects.forEach(cp => {
        const toP = planePos.clone().sub(cp.worldPos);
        const along = toP.dot(cp.normal);
        const radial = toP.clone().sub(cp.normal.clone().multiplyScalar(along)).length();
        const inside = Math.abs(along) < 6 && radial < cp.radius;

        if (inside && !cp.insideNow) {
          cp.insideNow = true;
          paused = true;

          if (!cp.visited) {
            cp.visited = true;
            cp.object.userData.mat.color.set(0x44ffaa);
            cp.object.userData.mat.emissive.set(0x22cc66);
            cp.object.userData.mat.emissiveIntensity = 2.5;
            const vc = cpObjects.filter(c => c.visited).length;
            setVC(vc);
          }

          setCP({ ...cp.data, _index: cp.index });
        } else if (!inside && cp.insideNow) {
          cp.insideNow = false;
        }
      });
    }

    function getNavData() {
      const nextCp = cpObjects.find(c => !c.visited);
      if (!nextCp) return { code: 'DONE', label: 'Complete', bearing: 0, distance: null, offCourse: false };
      const toT = nextCp.worldPos.clone().sub(planePos);
      const dist = toT.length();
      const q = planeGroup.quaternion;
      const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(q);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(q);
      const bearing = Math.atan2(toT.dot(right), toT.dot(fwd));
      let minD = Infinity;
      curveSamples.forEach(s => { const d = planePos.distanceTo(s); if (d < minD) minD = d; });
      const offCourse = minD > 320;
      if (minD > 640 && !paused) {
        planePos.copy(nextCp.worldPos.clone().addScaledVector(nextCp.normal, 38));
        headingAngle = Math.atan2(-nextCp.normal.x, -nextCp.normal.z);
        pitchAngle = 0; yawVel = 0; pitchVel = 0;
      }
      return { code: nextCp.data.code, label: nextCp.data.label, bearing, distance: dist, offCourse };
    }

    let hudTimer = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const delta = Math.min(0.05, clock.getDelta());

      updateFlight(delta);
      updateContrail(delta);
      updateCamera(false);
      updateSun();
      updateRings(delta);
      if (shakeTime > 0) shakeTime -= delta;

      const eT = clock.elapsedTime;
      labelSprites.forEach((ls, i) => {
        if (!ls || !ls.material) return;
        const wPos = labelWorldPos[i];
        if (!wPos) return;
        ls.position.set(wPos.x, wPos.y + 22 + Math.sin(eT * 1.3 + i * 0.9) * 1.4, wPos.z);
        ls.material.opacity = 0.75 + Math.sin(eT * 1.6 + i) * 0.25;
      });

      hudTimer += delta;
      if (hudTimer > 0.08) {
        hudTimer = 0;
        const nav = getNavData();
        const spd = Math.round(MIN_SPEED + (MAX_SPEED - MIN_SPEED) * throttle);
        setHudData({
          heading: headingAngle, altitude: planePos.y,
          speed: spd, throttle,
          checkpointsVisited: cpObjects.filter(c => c.visited).length,
          compassBearing: nav.bearing, distanceToNext: nav.distance,
          offCourse: nav.offCourse, pitch: pitchAngle, bank: bankAngle,
        });
      }

      renderer.render(scene, camera);
    };

    planeGroup.position.copy(planePos);
    updateCamera(true); updateSun(); animate();

    sceneRef.current = {
      resume: () => { paused = false; },
      resetFlight: () => {
        planePos.set(0, 22, 20); headingAngle = 0; pitchAngle = 0; yawVel = 0; pitchVel = 0; bankAngle = 0;
        throttle = 0.45; paused = false;
        cpObjects.forEach(cp => {
          cp.visited = false;
          cp.insideNow = false;
          cp.object.userData.mat.color.set(cp.data.color || '#667eea');
          cp.object.userData.mat.emissive.set(cp.data.color || '#667eea');
          cp.object.userData.mat.emissiveIntensity = 1.4;
          if (cp.object.userData.fill) cp.object.userData.fill.material.opacity = 0.22;
        });
        labelSprites.forEach(ls => { if (ls && ls.material) ls.material.opacity = 1; });
        setVisitedCount(0); setActiveCheckpoint(null); setMissionDone(false);
      },
    };

    // This interval only ever opens checkpoint panels. It deliberately
    // never sets missionDone itself. That used to happen here, and on the
    // final checkpoint it raced with setCP being called in the same tick:
    // activeCheckpoint and missionDone both became true at once, and since
    // CheckpointPanel only renders when "activeCheckpoint && !missionDone"
    // and FlightComplete only renders when "missionDone && !activeCheckpoint",
    // neither condition was true and the screen went blank with no way to
    // continue. Completion is now decided in exactly one place,
    // handleContinue, after the last checkpoint's panel has been dismissed.
    let colInterval = setInterval(() => {
      if (paused) return;
      checkCollisions(setActiveCheckpoint, setVisitedCount);
    }, 80);

    const handleResize = () => {
      const w = mount.clientWidth || window.innerWidth;
      const h = mount.clientHeight || window.innerHeight;
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);
    if (window.ResizeObserver) { const ro = new ResizeObserver(handleResize); ro.observe(mount); }

    return () => {
      cancelAnimationFrame(animId);
      clearInterval(colInterval);
      window.removeEventListener('resize', handleResize);
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = useCallback(() => {
    setActiveCheckpoint(null);
    if (sceneRef.current) sceneRef.current.resume();
    // The single place mission completion is decided: only once the last
    // checkpoint's panel has actually been read and dismissed.
    if (visitedCount >= CHECKPOINTS.length) setMissionDone(true);
  }, [visitedCount]);

  const handleViewInPortfolio = useCallback(() => {
    if (activeCheckpoint && onNavigateToSection) onNavigateToSection(activeCheckpoint.section);
    onExit();
  }, [activeCheckpoint, onNavigateToSection, onExit]);

  const handleReplay = useCallback(() => {
    if (sceneRef.current) sceneRef.current.resetFlight();
  }, []);

  return (
    <div className="flight-mode-root">
      <div ref={mountRef} className="flight-canvas-mount" />
      <div className="flight-flash-red" id="flight-flash-red" />

      <FlightHUD
        heading={hudData.heading}
        altitude={hudData.altitude}
        speed={hudData.speed}
        throttle={hudData.throttle}
        checkpointsTotal={CHECKPOINTS.length}
        checkpointsVisited={hudData.checkpointsVisited}
        nextCheckpoint={
          hudData.checkpointsVisited < CHECKPOINTS.length
            ? { code: CHECKPOINTS[hudData.checkpointsVisited]?.code, label: CHECKPOINTS[hudData.checkpointsVisited]?.label }
            : null
        }
        compassBearing={hudData.compassBearing}
        distanceToNext={hudData.distanceToNext}
        offCourse={hudData.offCourse}
        onExit={onExit}
        pitch={hudData.pitch}
        bank={hudData.bank}
        isMobile={isMobile}
      />

      {isMobile && !activeCheckpoint && !missionDone && (
        <MobileControls controlsRef={controlsRef} />
      )}

      <AnimatePresence>
        {showHint && !activeCheckpoint && !missionDone && (
          <motion.div className="flight-hint"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}>
            {isMobile
              ? <>Joystick to steer &middot; <strong>Slider</strong> = throttle &middot; Fly through the glowing rings, you can revisit any of them</>
              : <><strong>WASD / Arrows</strong> to steer &middot; <strong>[ ]</strong> throttle &middot; Fly through the glowing rings, you can revisit any of them</>
            }
          </motion.div>
        )}
      </AnimatePresence>

      {activeCheckpoint && !missionDone && (
        <CheckpointPanel checkpoint={activeCheckpoint} visitedCount={visitedCount}
          totalCount={CHECKPOINTS.length} onContinue={handleContinue}
          onViewInPortfolio={handleViewInPortfolio} />
      )}

      {missionDone && !activeCheckpoint && (
        <FlightComplete onReplay={handleReplay} onExit={onExit} />
      )}
    </div>
  );
};

export default FlightMode;